import type { ModelAiTaskType } from './ai-task-contracts';
import type { AiFeatureBillingOwner, AiFeatureQuoteDimension } from './ai-feature-routing';
import { z } from 'zod';

const safeInteger = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);

export const AiToolBillingModeSchema = z.enum([
  'free',
  'fixed_per_call',
  'actual_external_cost',
  'internal_usage_only',
]);

export type AiToolBillingMode = z.infer<typeof AiToolBillingModeSchema>;

const aiToolType = z.string().trim().min(1).max(120);

export const AiToolBillingRuleSchema = z
  .object({
    tool_type: aiToolType,
    mode: AiToolBillingModeSchema,
    fixed_milli_points: safeInteger.default(0),
    external_cost_points_per_usd: safeInteger.default(0),
  })
  .strict()
  .superRefine((rule, context) => {
    const fixedRequired = rule.mode === 'fixed_per_call';
    const externalRequired = rule.mode === 'actual_external_cost';
    if (fixedRequired && rule.fixed_milli_points < 1) {
      context.addIssue({
        code: 'custom',
        path: ['fixed_milli_points'],
        message: 'fixed_per_call requires a positive fixed_milli_points',
      });
    }
    if (!fixedRequired && rule.fixed_milli_points !== 0) {
      context.addIssue({
        code: 'custom',
        path: ['fixed_milli_points'],
        message: 'fixed_milli_points is only valid for fixed_per_call',
      });
    }
    if (externalRequired && rule.external_cost_points_per_usd < 1) {
      context.addIssue({
        code: 'custom',
        path: ['external_cost_points_per_usd'],
        message: 'actual_external_cost requires a positive external cost rate',
      });
    }
    if (!externalRequired && rule.external_cost_points_per_usd !== 0) {
      context.addIssue({
        code: 'custom',
        path: ['external_cost_points_per_usd'],
        message: 'external_cost_points_per_usd is only valid for actual_external_cost',
      });
    }
  });

export type AiToolBillingRule = z.input<typeof AiToolBillingRuleSchema>;

export const AiToolCallResultSchema = z.enum(['succeeded', 'failed']);
export type AiToolCallResult = z.infer<typeof AiToolCallResultSchema>;

export const AiToolCallEventSchema = z
  .object({
    event_id: z.string().trim().min(1).max(200),
    tenant_id: z.string().trim().min(1).max(80),
    reservation_id: z.string().trim().min(1).max(80),
    execution_id: z.string().trim().min(1).max(200),
    action_id: z.string().trim().min(1).max(200),
    message_id: z.string().trim().min(1).max(200),
    skill_id: z.string().trim().min(1).max(128).nullable().default(null),
    tool_call_id: z.string().trim().min(1).max(200),
    attempt_number: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    tool_type: aiToolType,
    tool_name: z.string().trim().min(1).max(120),
    result: AiToolCallResultSchema,
    cost_incurred: z.boolean(),
    actual_cost_micros: safeInteger.nullable().default(null),
    currency: z.literal('USD').nullable().default(null),
    occurred_at: z.string().datetime(),
  })
  .strict()
  .superRefine((event, context) => {
    if (event.actual_cost_micros !== null && event.currency !== 'USD') {
      context.addIssue({
        code: 'custom',
        path: ['currency'],
        message: 'actual external cost must use USD',
      });
    }
  });

export type AiToolCallEvent = z.output<typeof AiToolCallEventSchema>;
export type AiToolCallEventInput = z.input<typeof AiToolCallEventSchema>;

export interface AiToolBillingEstimate {
  tool_type: string;
  mode: AiToolBillingMode;
  cost_incurred: boolean;
  actual_cost_micros: number | null;
  currency: 'USD' | null;
  micro_points: number;
}

export interface AiToolBillingBreakdown {
  tool_type: string;
  tool_name: string;
  mode: AiToolBillingMode;
  skill_id: string | null;
  attempt_count: number;
  failed_count: number;
  micro_points: number;
}

export const AiBillingUsageSchema = z
  .object({
    output_units: safeInteger.min(1).default(1),
    input_tokens: safeInteger.default(0),
    output_tokens: safeInteger.default(0),
    cached_tokens: safeInteger.default(0),
    reasoning_tokens: safeInteger.default(0),
    image_input_units: safeInteger.default(0),
    image_output_units: safeInteger.default(0),
    audio_input_units: safeInteger.default(0),
    audio_output_units: safeInteger.default(0),
    tool_calls: safeInteger.default(0),
  })
  .strict();

export type AiBillingUsage = z.infer<typeof AiBillingUsageSchema>;

export const AiModelCallResultSchema = z.enum([
  'provider_succeeded',
  'provider_failed',
  'output_validation_failed',
  'business_validation_failed',
]);

export type AiModelCallResult = z.infer<typeof AiModelCallResultSchema>;

export const AiModelCallTokenUsageSchema = z
  .object({
    prompt_tokens: safeInteger.nullable(),
    completion_tokens: safeInteger.nullable(),
    cache_read_tokens: safeInteger.nullable(),
    cache_write_tokens: safeInteger.nullable(),
    reasoning_tokens: safeInteger.nullable(),
    total_tokens: safeInteger.nullable(),
  })
  .strict();

export type AiModelCallTokenUsage = z.infer<typeof AiModelCallTokenUsageSchema>;

export const AiBillingRatesSchema = z
  .object({
    input_token_milli_points: safeInteger,
    output_token_milli_points: safeInteger,
    cached_token_milli_points: safeInteger,
    reasoning_token_milli_points: safeInteger,
    image_input_milli_points: safeInteger,
    image_output_milli_points: safeInteger,
    audio_input_milli_points: safeInteger,
    audio_output_milli_points: safeInteger,
    tool_call_milli_points: safeInteger,
  })
  .strict();

export const AiBillingPolicySchema = z
  .object({
    mode: z.enum(['fixed', 'metered', 'hybrid']),
    rounding: z.enum(['floor', 'ceil', 'nearest']),
    min_points: safeInteger.min(1),
    max_points: safeInteger.min(1).nullable(),
    tool_billing_enabled: z.boolean(),
    // These fields were added after the first publication snapshots.  Keep
    // them defaulted in the shared parser so an old immutable snapshot is
    // still a valid policy with the legacy fixed behaviour.
    model_multiplier_enabled: z.boolean().default(true),
    reservation_buffer_milli: safeInteger.min(1_000).max(10_000).default(1_000),
    tool_rules: z
      .array(AiToolBillingRuleSchema)
      .max(100)
      .default([])
      .superRefine((rules, context) => {
        const seen = new Set<string>();
        rules.forEach((rule, index) => {
          if (seen.has(rule.tool_type)) {
            context.addIssue({
              code: 'custom',
              path: [index, 'tool_type'],
              message: 'tool_type rules must be unique',
            });
          }
          seen.add(rule.tool_type);
        });
      }),
    rates: AiBillingRatesSchema,
  })
  .strict()
  .refine((policy) => policy.max_points === null || policy.max_points >= policy.min_points, {
    message: 'max_points must be greater than or equal to min_points',
    path: ['max_points'],
  });

// `z.input` deliberately keeps the two versioned additions optional at the
// TypeScript boundary.  Existing callers may still construct an old policy
// literal; every runtime entry point calls parseAiBillingPolicy and receives
// the normalized defaults above.
export type AiBillingPolicy = z.input<typeof AiBillingPolicySchema>;

/**
 * 用户侧费用明细只暴露结构化用量和按模型倍率加权后的微点数，避免
 * 用四舍五入后的各项整数反推一个与最终扣点不一致的总额。
 */
export interface AiBillingBreakdown {
  mode: AiBillingPolicy['mode'];
  attempt_count: number;
  model_id: string | null;
  model_multiplier_milli: number | null;
  usage: AiBillingUsage;
  components_micro_points: {
    base: number;
    input_tokens: number;
    output_tokens: number;
    cached_tokens: number;
    reasoning_tokens: number;
    image_input_units: number;
    image_output_units: number;
    audio_input_units: number;
    audio_output_units: number;
    tool_calls: number;
  };
  tool_details: AiToolBillingBreakdown[];
  total_points: number;
  reserved_points: number | null;
  settled_points: number | null;
  released_points: number | null;
}

export const LEGACY_FIXED_AI_BILLING_POLICY: AiBillingPolicy = {
  mode: 'fixed',
  rounding: 'floor',
  min_points: 1,
  max_points: null,
  tool_billing_enabled: false,
  model_multiplier_enabled: true,
  reservation_buffer_milli: 1_000,
  tool_rules: [],
  rates: {
    input_token_milli_points: 0,
    output_token_milli_points: 0,
    cached_token_milli_points: 0,
    reasoning_token_milli_points: 0,
    image_input_milli_points: 0,
    image_output_milli_points: 0,
    audio_input_milli_points: 0,
    audio_output_milli_points: 0,
    tool_call_milli_points: 0,
  },
};

export function parseAiBillingPolicy(value: unknown): AiBillingPolicy {
  return AiBillingPolicySchema.parse(value);
}

export function parseAiBillingUsage(value: unknown): AiBillingUsage {
  return AiBillingUsageSchema.parse(value);
}

export function readPublishedAiBillingPolicy(value: unknown): AiBillingPolicy {
  return value === undefined ? LEGACY_FIXED_AI_BILLING_POLICY : parseAiBillingPolicy(value);
}

export type AiTriggerSource = 'manual' | 'automatic' | 'internal';

export interface AiBillingContract {
  billing_owner: AiFeatureBillingOwner;
  quote_dimension: AiFeatureQuoteDimension;
}

export function resolveAiBillingContract(
  taskType: ModelAiTaskType,
  trigger: AiTriggerSource,
): AiBillingContract {
  if (trigger === 'internal' || (taskType === 'daily_recommendation' && trigger === 'automatic')) {
    return { billing_owner: 'platform', quote_dimension: 'output_units' };
  }
  return { billing_owner: 'user_points', quote_dimension: 'output_units' };
}

export interface FutureChatQuoteExtension {
  quote_dimension: 'chat_messages';
  user_messages: number;
  model_id: string;
}

export interface FutureImageQuoteExtension {
  quote_dimension: 'images';
  image_count: number;
  model_id: string;
  resolution: string;
  quality: string;
}
